import { EMPTY_INPUT, type GameMode, type MatchEvent, type MatchState } from "../game/simulation";
import { InputController } from "../game/input/InputController";
import { ArenaRenderer } from "../phaser/createArenaRenderer";
import { toArenaViewModel } from "../phaser/viewModel";
import { LocalMatchDriver } from "../network/LocalMatchDriver";
import {
  MatchSocket,
  playerSocketUrl,
  spectatorSocketUrl,
  type ConnectionStatus
} from "../network/MatchSocket";
import { fetchActiveMatches, type ActiveMatchSummary } from "../network/adminApi";

interface AdminSession {
  id: string;
  node: HTMLElement;
  renderer: ArenaRenderer;
  latestState: MatchState | null;
  socket?: MatchSocket;
  driver?: LocalMatchDriver;
}

interface InviteCredentials {
  matchId: string;
  ticket: string;
  bracketRound?: string;
  tournamentStatus?: string;
}

export class FoamArenaApp {
  private readonly input = new InputController();
  private renderer: ArenaRenderer | null = null;
  private localDriver: LocalMatchDriver | null = null;
  private socket: MatchSocket | null = null;
  private adminSessions = new Map<string, AdminSession>();
  private adminRefreshTimer: number | null = null;
  private latestState: MatchState | null = null;
  private lastPhase = "";
  private audioContext: AudioContext | null = null;
  private finalAssignmentPollTimer: number | null = null;
  private finalAssignmentPollVersion = 0;

  constructor(private readonly root: HTMLElement) {}

  mount(): void {
    if (location.pathname === "/admin" || location.pathname.startsWith("/admin/")) {
      void this.renderAdmin();
    } else {
      this.renderLanding();
    }
  }

  private renderLanding(): void {
    this.cleanupActiveView();
    const query = new URLSearchParams(location.search);
    // `?match=` is the durable resume URL. Its HttpOnly invitation session can
    // mint a fresh short-lived socket ticket after reload or tab eviction.
    const hasInvite = Boolean(query.get("invite") || query.get("token") || query.get("match"));
    this.root.className = "app app--landing";
    this.root.innerHTML = `
      <main class="landing-shell">
        <section class="brand-stage" aria-labelledby="game-title">
          <div class="brand-mark" aria-hidden="true">
            <span></span><span></span><span></span>
          </div>
          <p class="eyebrow">PRIVATE ARENA · GREYBOX 0.1</p>
          <h1 id="game-title">FOAM<br /><em>ARENA</em></h1>
          <p class="brand-copy">포획하고, 구조하고, 먼저 터뜨리세요.<br />최대 네 명을 위한 비공개 팀 아레나입니다.</p>
          <div class="system-strip" aria-label="시스템 상태">
            <span><i class="signal-dot signal-dot--live"></i>SERVER AUTHORITY</span>
            <span>4 SEATS MAX</span>
            <span>30 TICK</span>
          </div>
        </section>

        <section class="launch-panel" aria-label="게임 시작 메뉴">
          <div class="launch-panel__heading">
            <span class="panel-index">01</span>
            <div>
              <p class="eyebrow">SELECT SESSION</p>
              <h2>경기 시작</h2>
            </div>
          </div>

          ${hasInvite ? `
            <button class="launch-card launch-card--invite" data-launch="invite">
              <span class="launch-card__number">INVITE</span>
              <span class="launch-card__body"><strong>초대 경기 접속</strong><small>이 기기의 지정 좌석으로 연결</small></span>
              <span class="launch-card__arrow">→</span>
            </button>
          ` : ""}

          <button class="launch-card" data-launch="duel">
            <span class="launch-card__number">1:1</span>
            <span class="launch-card__body"><strong>듀얼 연습</strong><small>YOU vs MICA · 로컬 AI</small></span>
            <span class="launch-card__arrow">→</span>
          </button>

          <button class="launch-card" data-launch="team">
            <span class="launch-card__number">2:2</span>
            <span class="launch-card__body"><strong>팀전 연습</strong><small>YOU + AI vs AI + AI</small></span>
            <span class="launch-card__arrow">→</span>
          </button>

          <label class="immersive-toggle">
            <input type="checkbox" id="fullscreen-start" />
            <span class="toggle-track"><span></span></span>
            <span><strong>전체 화면으로 시작</strong><small>지원되는 브라우저에서만 적용</small></span>
          </label>

          <div class="quick-controls">
            <span>이동 <kbd>A</kbd><kbd>D</kbd></span>
            <span>점프 <kbd>SPACE</kbd></span>
            <span>발사 <kbd>F</kbd></span>
          </div>
          <p class="launch-error" role="alert" aria-live="polite"></p>
        </section>
      </main>
      ${this.orientationGuard()}
    `;

    this.root.querySelectorAll<HTMLButtonElement>("[data-launch]").forEach((button) => {
      button.addEventListener("click", () => {
        const mode = button.dataset.launch;
        void this.prepareStart().then(() => {
          if (mode === "invite") void this.startInvitedMatch();
          else this.startLocalMatch(mode === "team" ? "team" : "duel");
        });
      });
    });
  }

  private startLocalMatch(mode: GameMode): void {
    this.renderPlayerShell(mode === "team" ? "2:2 LOCAL SCRIMMAGE" : "1:1 LOCAL DUEL", "local");
    const arenaHost = this.required<HTMLElement>("#arena-host");
    this.input.attachTouchControls(this.required<HTMLElement>("#touch-controls"));
    this.input.setEnabled(true);

    this.renderer = new ArenaRenderer({
      parent: arenaHost,
      readInput: () => this.input.read()
    });
    this.localDriver = new LocalMatchDriver({
      mode,
      readHumanInput: () => this.input.read(),
      onSnapshot: (state) => this.publishPlayerState(state),
      onEvents: (events) => this.publishEvents(events)
    });
    this.localDriver.start();
    this.required<HTMLButtonElement>("#reset-match").hidden = false;
    this.required<HTMLButtonElement>("#reset-match").addEventListener("click", () => this.localDriver?.reset());
  }

  private async startInvitedMatch(): Promise<void> {
    const error = this.root.querySelector<HTMLElement>(".launch-error");
    try {
      const credentials = await this.resolveInviteCredentials();
      let assignedMatchId = credentials.matchId;
      let oneTimeCredentials: InviteCredentials | null = credentials;
      let observedPhase = "";
      this.renderPlayerShell("INVITED MATCH", "connecting");
      const arenaHost = this.required<HTMLElement>("#arena-host");
      this.input.attachTouchControls(this.required<HTMLElement>("#touch-controls"));
      this.input.setEnabled(true);
      this.socket = new MatchSocket({
        url: async () => {
          if (oneTimeCredentials) {
            const initial = oneTimeCredentials;
            oneTimeCredentials = null;
            assignedMatchId = initial.matchId;
            return playerSocketUrl(initial.matchId, initial.ticket);
          }
          const renewed = await this.requestWsCredentials(assignedMatchId);
          assignedMatchId = renewed.matchId;
          return playerSocketUrl(renewed.matchId, renewed.ticket);
        },
        onSnapshot: (state, playerId) => {
          assignedMatchId = state.id;
          this.publishPlayerState(state);
          const playerTeam = state.players.find((player) => player.id === playerId)?.team;
          const wonMatch = playerTeam !== undefined && state.matchWinner === playerTeam;
          if (state.phase === "match_end" && observedPhase !== "match_end" && wonMatch) {
            const endedMatchId = state.id;
            this.startFinalAssignmentPoll(endedMatchId, (next) => {
              assignedMatchId = next.matchId;
              oneTimeCredentials = next;
              history.replaceState({}, "", `${location.pathname}?match=${encodeURIComponent(next.matchId)}`);
              this.socket?.reconnectNow();
            });
          } else if (state.phase !== "match_end" || !wonMatch) {
            this.stopFinalAssignmentPoll();
          }
          observedPhase = state.phase;
        },
        onEvents: (events) => this.publishEvents(events),
        onStatus: (status, latency) => this.updateConnection(status, latency),
        onError: (message) => this.showGameNotice(message, "danger")
      });
      this.renderer = new ArenaRenderer({
        parent: arenaHost,
        readInput: () => this.input.read(),
        onInput: (input) => this.socket?.pushInput(input)
      });
      this.socket.connect();
      history.replaceState({}, "", `${location.pathname}?match=${encodeURIComponent(credentials.matchId)}`);
    } catch (cause) {
      if (error) error.textContent = cause instanceof Error ? cause.message : "초대 링크를 확인할 수 없습니다.";
    }
  }

  private renderPlayerShell(title: string, connection: string): void {
    this.cleanupActiveView();
    this.root.className = "app app--game";
    this.root.innerHTML = `
      <main class="game-shell">
        <header class="match-hud" aria-label="경기 상태">
          <section class="team-panel team-panel--blue">
            <div class="team-panel__score"><span>BLUE</span><strong id="score-blue">0</strong><small id="wins-blue">ROUND 0</small></div>
            <div class="roster" id="roster-blue"></div>
          </section>
          <section class="match-clock">
            <p id="match-mode">${title}</p>
            <strong id="round-time">75</strong>
            <span id="round-label">ROUND 1 · FIRST TO 3</span>
          </section>
          <section class="team-panel team-panel--orange">
            <div class="roster roster--right" id="roster-orange"></div>
            <div class="team-panel__score"><span>ORANGE</span><strong id="score-orange">0</strong><small id="wins-orange">ROUND 0</small></div>
          </section>
        </header>

        <section class="play-surface" aria-label="Foam Arena 경기장">
          <div id="arena-host" class="arena-host"></div>
          <div id="round-notice" class="round-notice" aria-live="polite"></div>
          <div id="event-toast" class="event-toast" aria-live="polite"></div>
          ${this.touchControls()}
        </section>

        <footer class="match-utility">
          <div class="connection-chip" id="connection-chip" data-state="${connection}">
            <i class="signal-dot"></i><span>${connection === "local" ? "LOCAL SIM" : "CONNECTING"}</span>
          </div>
          <p class="control-hint">← → 이동 · SPACE 점프 · F 포획 구체</p>
          <div class="utility-actions">
            <button id="reset-match" class="icon-button" type="button" hidden>재시작</button>
            <button id="exit-match" class="icon-button" type="button">나가기</button>
          </div>
        </footer>
      </main>
      ${this.orientationGuard()}
    `;
    this.required<HTMLButtonElement>("#exit-match").addEventListener("click", () => this.renderLanding());
  }

  private publishPlayerState(state: MatchState): void {
    this.latestState = state;
    this.renderer?.publish(state);
    const view = toArenaViewModel(state);
    this.setText("#score-blue", String(view.score.A));
    this.setText("#score-orange", String(view.score.B));
    this.setText("#wins-blue", `ROUND ${view.roundWins.A}`);
    this.setText("#wins-orange", `ROUND ${view.roundWins.B}`);
    this.setText("#round-time", String(Math.ceil(view.roundTimeSeconds)));
    this.setText("#round-label", `ROUND ${view.round} · FIRST TO ${view.scoreTarget}`);
    this.updateRoster("#roster-blue", view.players.filter((player) => player.team === "A"));
    this.updateRoster("#roster-orange", view.players.filter((player) => player.team === "B"));

    if (view.phase !== this.lastPhase) {
      this.lastPhase = view.phase;
      if (view.phase === "countdown") this.showRoundNotice(`ROUND ${view.round}`);
      if (view.phase === "round_end") this.showRoundNotice("ROUND COMPLETE");
      if (view.phase === "match_end") {
        const winner = state.matchWinner === "blue" ? "BLUE" : "ORANGE";
        this.showRoundNotice(`${winner} WINS`);
      }
    }
  }

  private updateRoster(selector: string, players: ReturnType<typeof toArenaViewModel>["players"]): void {
    const roster = this.root.querySelector<HTMLElement>(selector);
    if (!roster) return;
    roster.replaceChildren(...players.map((player) => {
      const item = document.createElement("span");
      item.className = `roster-player${player.connected ? "" : " is-offline"}`;
      item.textContent = `${player.name} · ${player.kind === "ai" ? "AI" : "HUMAN"}`;
      return item;
    }));
  }

  private publishEvents(events: MatchEvent[]): void {
    const important = [...events].reverse().find((event) =>
      event.type === "captured" || event.type === "rescued" || event.type === "popped" || event.type === "golden_pop_started"
    );
    if (!important) return;
    const label = important.type === "captured" ? "CAPTURE"
      : important.type === "rescued" ? "RESCUE"
      : important.type === "popped" ? "POP +1"
      : "GOLDEN POP";
    const toast = this.root.querySelector<HTMLElement>("#event-toast");
    if (!toast) return;
    toast.textContent = label;
    toast.classList.remove("is-visible");
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    window.setTimeout(() => toast.classList.remove("is-visible"), 850);
  }

  private updateConnection(status: ConnectionStatus, latency?: number): void {
    const chip = this.root.querySelector<HTMLElement>("#connection-chip");
    if (!chip) return;
    chip.dataset.state = status;
    const label = chip.querySelector("span");
    if (label) {
      label.textContent = status === "live" ? `LIVE${latency === undefined ? "" : ` · ${Math.round(latency)}MS`}`
        : status === "reconnecting" ? "RECONNECTING"
        : status.toUpperCase();
    }
  }

  private async renderAdmin(): Promise<void> {
    this.cleanupActiveView();
    this.root.className = "app app--admin";
    this.root.innerHTML = `
      <main class="admin-shell">
        <header class="admin-header">
          <div>
            <p class="eyebrow">READ-ONLY CONTROL ROOM</p>
            <h1>LIVE ARENAS</h1>
          </div>
          <div class="admin-header__status">
            <span id="admin-feed-status"><i class="signal-dot"></i>SYNCING</span>
            <button id="refresh-admin" class="icon-button">새로고침</button>
            <a href="/" class="icon-button">플레이 화면</a>
          </div>
        </header>
        <section id="admin-grid" class="admin-grid" aria-live="polite">
          <div class="admin-empty"><span class="loading-ring"></span><strong>경기 피드를 확인하는 중</strong></div>
        </section>
      </main>
      ${this.orientationGuard()}
    `;
    this.required<HTMLButtonElement>("#refresh-admin").addEventListener("click", () => void this.refreshAdminMatches(true));
    await this.refreshAdminMatches(true);
    this.adminRefreshTimer = window.setInterval(() => void this.refreshAdminMatches(false), 8000);
  }

  private async refreshAdminMatches(force: boolean): Promise<void> {
    let matches: ActiveMatchSummary[];
    let usingPreview = false;
    try {
      matches = await fetchActiveMatches();
      this.setAdminFeedStatus("LIVE INDEX", "live");
    } catch (cause) {
      if (!this.isLocalHost()) {
        this.setAdminFeedStatus("FEED ERROR", "error");
        this.showAdminEmpty(cause instanceof Error ? cause.message : "경기 목록을 불러올 수 없습니다.");
        return;
      }
      usingPreview = true;
      matches = this.previewMatches();
      this.setAdminFeedStatus("LOCAL PREVIEW", "preview");
    }

    if (matches.length === 0 && this.isLocalHost()) {
      matches = this.previewMatches();
      usingPreview = true;
      this.setAdminFeedStatus("LOCAL PREVIEW", "preview");
    }
    const ids = matches.map((match) => match.id).join("|");
    const existingIds = [...this.adminSessions.keys()].join("|");
    if (!force && ids === existingIds) return;
    this.destroyAdminSessions();

    const grid = this.required<HTMLElement>("#admin-grid");
    grid.replaceChildren();
    if (matches.length === 0) {
      this.showAdminEmpty("현재 진행 중인 경기가 없습니다.");
      return;
    }
    grid.classList.toggle("admin-grid--single", matches.length === 1);
    for (const match of matches) {
      this.createAdminCard(match, usingPreview);
    }
  }

  private createAdminCard(summary: ActiveMatchSummary, preview: boolean): void {
    const grid = this.required<HTMLElement>("#admin-grid");
    const card = document.createElement("article");
    card.className = "spectator-card";
    card.dataset.matchId = summary.id;
    card.innerHTML = `
      <header class="spectator-card__header">
        <div><span class="match-code"></span><strong></strong></div>
        <div class="spectator-card__score"><b data-score-blue>0</b><span>:</span><b data-score-orange>0</b></div>
        <button class="focus-button" type="button">전체 화면 ↗</button>
      </header>
      <div class="spectator-canvas"></div>
      <aside class="spectator-events" aria-label="주요 경기 이벤트">
        <span>EVENT LOG</span>
        <ol data-event-log aria-live="polite" aria-relevant="additions">
          <li class="is-empty">포획·구조·POP 이벤트 대기 중</li>
        </ol>
      </aside>
      <footer class="spectator-card__footer">
        <span data-round>ROUND 1</span>
        <span data-time>75 SEC</span>
        <span data-connection><i class="signal-dot"></i>${preview ? "LOCAL PREVIEW" : "CONNECTING"}</span>
      </footer>
    `;
    const code = card.querySelector<HTMLElement>(".match-code");
    const mode = card.querySelector<HTMLElement>(".spectator-card__header strong");
    if (code) code.textContent = summary.id;
    if (mode) mode.textContent = summary.mode === "2v2" ? "TEAM MATCH" : "DUEL";
    grid.append(card);

    const canvasHost = card.querySelector<HTMLElement>(".spectator-canvas");
    if (!canvasHost) return;
    const renderer = new ArenaRenderer({ parent: canvasHost, compact: true });
    const session: AdminSession = { id: summary.id, node: card, renderer, latestState: null };
    this.adminSessions.set(summary.id, session);

    card.querySelector<HTMLButtonElement>(".focus-button")?.addEventListener("click", () => {
      void this.focusSpectatorCard(card);
    });

    if (preview) {
      const driver = new LocalMatchDriver({
        mode: summary.mode === "2v2" ? "team" : "duel",
        readHumanInput: () => ({ ...EMPTY_INPUT }),
        automateHuman: true,
        onSnapshot: (state) => {
          session.latestState = state;
          renderer.publish(state);
          this.updateAdminCard(card, state);
        },
        onEvents: (events) => this.appendAdminEvents(card, events, session.latestState)
      });
      session.driver = driver;
      driver.start();
      return;
    }

    const socket = new MatchSocket({
      url: spectatorSocketUrl(summary.id),
      spectator: true,
      onSnapshot: (state) => {
        session.latestState = state;
        renderer.publish(state);
        this.updateAdminCard(card, state);
      },
      onEvents: (events) => this.appendAdminEvents(card, events, session.latestState),
      onStatus: (status, latency) => {
        const node = card.querySelector<HTMLElement>("[data-connection]");
        if (node) node.textContent = status === "live" ? `LIVE${latency === undefined ? "" : ` · ${Math.round(latency)}MS`}` : status.toUpperCase();
      },
      onError: (message) => {
        const node = card.querySelector<HTMLElement>("[data-connection]");
        if (node) node.textContent = message;
      }
    });
    session.socket = socket;
    socket.connect();
  }

  private updateAdminCard(card: HTMLElement, state: MatchState): void {
    const view = toArenaViewModel(state);
    this.setScopedText(card, "[data-score-blue]", String(view.score.A));
    this.setScopedText(card, "[data-score-orange]", String(view.score.B));
    this.setScopedText(card, "[data-round]", `ROUND ${view.round} · ${view.phase.toUpperCase()}`);
    this.setScopedText(card, "[data-time]", `${Math.ceil(view.roundTimeSeconds)} SEC`);
  }

  private appendAdminEvents(card: HTMLElement, events: MatchEvent[], state: MatchState | null): void {
    const log = card.querySelector<HTMLOListElement>("[data-event-log]");
    if (!log) return;
    const playerName = (playerId: string): string =>
      state?.players.find((player) => player.id === playerId)?.name ?? playerId;

    for (const event of events) {
      let label: string | null = null;
      let tone = "neutral";
      switch (event.type) {
        case "captured":
          label = `${playerName(event.playerId)} CAPTURE · ${playerName(event.byPlayerId)}`;
          tone = "capture";
          break;
        case "rescued":
          label = `${playerName(event.byPlayerId)} RESCUE → ${playerName(event.playerId)}`;
          tone = "rescue";
          break;
        case "popped":
          label = `${playerName(event.byPlayerId)} POP +1 · ${event.team.toUpperCase()}`;
          tone = event.team;
          break;
        case "golden_pop_started":
          label = "GOLDEN POP START";
          tone = "golden";
          break;
        default:
          break;
      }
      if (!label) continue;
      log.querySelector(".is-empty")?.remove();
      const item = document.createElement("li");
      item.dataset.tone = tone;
      const tick = document.createElement("small");
      tick.textContent = `T${event.tick}`;
      const text = document.createElement("strong");
      text.textContent = label;
      item.append(tick, text);
      log.prepend(item);
      while (log.children.length > 6) log.lastElementChild?.remove();
    }
  }

  private async focusSpectatorCard(card: HTMLElement): Promise<void> {
    const isFocused = card.classList.toggle("is-focused");
    this.required<HTMLElement>("#admin-grid").classList.toggle("has-focus", isFocused);
    const button = card.querySelector<HTMLButtonElement>(".focus-button");
    if (button) button.textContent = isFocused ? "분할 화면으로 ↙" : "전체 화면 ↗";
    if (isFocused && document.fullscreenEnabled) {
      try {
        await card.requestFullscreen({ navigationUI: "hide" });
      } catch {
        // CSS focus mode remains available when browser fullscreen is denied.
      }
    } else if (!isFocused && document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
    }
  }

  private async resolveInviteCredentials(): Promise<InviteCredentials> {
    const query = new URLSearchParams(location.search);
    const invite = query.get("invite") ?? query.get("token");
    let matchId = query.get("match") ?? "";
    let ticket = query.get("ticket") ?? "";

    if (invite) {
      const response = await fetch("/api/invites/redeem", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ token: invite })
      });
      if (!response.ok) throw new Error(`초대 승인 실패 (${response.status})`);
      const body = await response.json() as Record<string, unknown>;
      const player = body.player !== null && typeof body.player === "object"
        ? body.player as Record<string, unknown>
        : {};
      matchId = typeof body.matchId === "string" ? body.matchId
        : typeof player.matchId === "string" ? player.matchId
        : matchId;
      ticket = typeof body.ticket === "string" ? body.ticket
        : typeof body.wsTicket === "string" ? body.wsTicket
        : ticket;
    }

    if (matchId && !ticket) {
      const renewed = await this.requestWsCredentials(matchId);
      matchId = renewed.matchId;
      ticket = renewed.ticket;
    }
    if (!matchId || !ticket) throw new Error("초대 링크에 경기 정보가 없습니다.");
    return { matchId, ticket };
  }

  private async requestWsCredentials(matchId: string): Promise<InviteCredentials> {
    const response = await fetch("/api/ws-ticket", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ matchId })
    });
    if (!response.ok) throw new Error(`경기 티켓 발급 실패 (${response.status})`);
    const body = await response.json() as Record<string, unknown>;
    const player = body.player !== null && typeof body.player === "object"
      ? body.player as Record<string, unknown>
      : {};
    const renewedMatchId = typeof body.matchId === "string" ? body.matchId
      : typeof player.matchId === "string" ? player.matchId
      : matchId;
    const ticket = typeof body.ticket === "string" ? body.ticket
      : typeof body.wsTicket === "string" ? body.wsTicket
      : "";
    if (!renewedMatchId || !ticket) throw new Error("서버가 유효한 경기 티켓을 반환하지 않았습니다.");
    const credentials: InviteCredentials = { matchId: renewedMatchId, ticket };
    if (typeof body.bracketRound === "string") credentials.bracketRound = body.bracketRound;
    if (typeof body.tournamentStatus === "string") credentials.tournamentStatus = body.tournamentStatus;
    return credentials;
  }

  private startFinalAssignmentPoll(
    endedMatchId: string,
    onChanged: (credentials: InviteCredentials) => void
  ): void {
    this.stopFinalAssignmentPoll();
    const version = this.finalAssignmentPollVersion;
    const poll = async (): Promise<void> => {
      if (version !== this.finalAssignmentPollVersion || !this.socket) return;
      try {
        const credentials = await this.requestWsCredentials(endedMatchId);
        if (credentials.matchId !== endedMatchId) {
          this.stopFinalAssignmentPoll();
          onChanged(credentials);
          return;
        }
        // Only semifinal winners can receive another assignment. A final or
        // standalone/team match ends this polling loop after one metadata read.
        if (credentials.bracketRound && credentials.bracketRound !== "semifinal") {
          this.stopFinalAssignmentPoll();
          return;
        }
        if (credentials.tournamentStatus === "complete" || credentials.tournamentStatus === "cancelled") {
          this.stopFinalAssignmentPoll();
          return;
        }
      } catch {
        // Tournament advancement can briefly race the match result write.
      }
      if (version !== this.finalAssignmentPollVersion) return;
      // The other semifinal can legitimately last several minutes, especially
      // in Golden Pop. Keep a low-rate poll alive until view cleanup or reassignment.
      this.finalAssignmentPollTimer = window.setTimeout(() => void poll(), 2500);
    };
    this.finalAssignmentPollTimer = window.setTimeout(() => void poll(), 750);
  }

  private stopFinalAssignmentPoll(): void {
    this.finalAssignmentPollVersion += 1;
    if (this.finalAssignmentPollTimer !== null) window.clearTimeout(this.finalAssignmentPollTimer);
    this.finalAssignmentPollTimer = null;
  }

  private async prepareStart(): Promise<void> {
    const fullscreen = this.root.querySelector<HTMLInputElement>("#fullscreen-start")?.checked ?? false;
    const work: Promise<unknown>[] = [this.unlockAudio()];
    if (fullscreen && document.fullscreenEnabled) {
      work.push(document.documentElement.requestFullscreen({ navigationUI: "hide" }));
    }
    await Promise.allSettled(work);
  }

  private async unlockAudio(): Promise<void> {
    this.audioContext ??= new AudioContext();
    if (this.audioContext.state === "suspended") await this.audioContext.resume();
    const oscillator = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(660, this.audioContext.currentTime + 0.08);
    gain.gain.setValueAtTime(0.0001, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.04, this.audioContext.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.audioContext.currentTime + 0.1);
    oscillator.connect(gain).connect(this.audioContext.destination);
    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.11);
  }

  private touchControls(): string {
    return `
      <div id="touch-controls" class="touch-controls" aria-label="터치 조작">
        <div class="touch-cluster touch-cluster--move">
          <button data-action="left" aria-label="왼쪽 이동" aria-pressed="false">←</button>
          <button class="touch-drop" data-action="drop" aria-label="플랫폼 아래로 내려가기" aria-pressed="false">↓</button>
          <button data-action="right" aria-label="오른쪽 이동" aria-pressed="false">→</button>
        </div>
        <div class="touch-cluster touch-cluster--action">
          <button data-action="jump" aria-label="점프" aria-pressed="false"><span>JUMP</span>△</button>
          <button data-action="fire" aria-label="포획 구체 발사" aria-pressed="false"><span>FOAM</span>○</button>
        </div>
      </div>
    `;
  }

  private orientationGuard(): string {
    return `
      <aside class="orientation-guard" role="status">
        <div class="rotate-device" aria-hidden="true"><span></span></div>
        <strong>기기를 가로로 돌려주세요</strong>
        <p>한 명당 한 기기, 가로 화면에 맞춰진 아레나입니다.</p>
      </aside>
    `;
  }

  private showRoundNotice(message: string): void {
    const notice = this.root.querySelector<HTMLElement>("#round-notice");
    if (!notice) return;
    notice.textContent = message;
    notice.classList.add("is-visible");
    window.setTimeout(() => notice.classList.remove("is-visible"), 1050);
  }

  private showGameNotice(message: string, tone: "danger" | "info"): void {
    const notice = this.root.querySelector<HTMLElement>("#round-notice");
    if (!notice) return;
    notice.textContent = message;
    notice.dataset.tone = tone;
    notice.classList.add("is-visible");
  }

  private previewMatches(): ActiveMatchSummary[] {
    return [
      { id: "SF-A · DEMO", gameKind: "arena", mode: "1v1", phase: "playing", playerCount: 2 },
      { id: "SF-B · DEMO", gameKind: "arena", mode: "2v2", phase: "playing", playerCount: 4 }
    ];
  }

  private showAdminEmpty(message: string): void {
    const grid = this.root.querySelector<HTMLElement>("#admin-grid");
    if (!grid) return;
    const empty = document.createElement("div");
    empty.className = "admin-empty";
    const strong = document.createElement("strong");
    strong.textContent = message;
    const small = document.createElement("small");
    small.textContent = "경기가 시작되면 자동으로 이 화면에 나타납니다.";
    empty.append(strong, small);
    grid.replaceChildren(empty);
  }

  private setAdminFeedStatus(label: string, state: string): void {
    const node = this.root.querySelector<HTMLElement>("#admin-feed-status");
    if (!node) return;
    node.dataset.state = state;
    node.innerHTML = `<i class="signal-dot"></i>${label}`;
  }

  private cleanupActiveView(): void {
    this.stopFinalAssignmentPoll();
    this.input.setEnabled(false);
    this.renderer?.destroy();
    this.renderer = null;
    this.localDriver?.stop();
    this.localDriver = null;
    this.socket?.close();
    this.socket = null;
    this.latestState = null;
    this.lastPhase = "";
    this.destroyAdminSessions();
    if (this.adminRefreshTimer !== null) window.clearInterval(this.adminRefreshTimer);
    this.adminRefreshTimer = null;
  }

  private destroyAdminSessions(): void {
    this.adminSessions.forEach((session) => {
      session.driver?.stop();
      session.socket?.close();
      session.renderer.destroy();
    });
    this.adminSessions.clear();
  }

  private isLocalHost(): boolean {
    return import.meta.env.DEV || location.hostname === "localhost" || location.hostname === "127.0.0.1";
  }

  private setText(selector: string, value: string): void {
    const node = this.root.querySelector<HTMLElement>(selector);
    if (node) node.textContent = value;
  }

  private setScopedText(root: HTMLElement, selector: string, value: string): void {
    const node = root.querySelector<HTMLElement>(selector);
    if (node) node.textContent = value;
  }

  private required<T extends Element>(selector: string): T {
    const node = this.root.querySelector<T>(selector);
    if (!node) throw new Error(`Required UI element missing: ${selector}`);
    return node;
  }
}
